import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: 'website' | 'article' | 'product';
  ogImage?: string;
  noIndex?: boolean;
  structuredData?: object;
}

const BASE_URL = 'https://ochtopus.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

export function SEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  noIndex = false,
  structuredData,
}: SEOProps) {
  const fullTitle = title.includes('Ochtopus') ? title : `${title} | Ochtopus`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : undefined;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Ochtopus" />
      <meta property="og:locale" content="tr_TR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

// Pre-defined SEO configurations for each page
export const SEO_CONFIG = {
  home: {
    title: 'Ochtopus - Yapay Zeka Destekli Kod Editoru',
    description: 'Ochtopus, sizi olagnustu uretken yapan yapay zeka destekli kod editorudur. Dahili AI Agent, akilli otomatik tamamlama ve kesintisiz Git entegrasyonu.',
    keywords: 'kod editoru, IDE, yapay zeka kodlama, gelistirici araclari, programlama, AI agent, otomatik tamamlama, VS Code alternatifi, Ochtopus',
    canonical: '/',
  },
  features: {
    title: 'Ozellikler - Yapay Zeka Destekli Kodlama',
    description: 'Ochtopus ozellikleri: AI Agent, akilli kod tamamlama, dahili test, Git entegrasyonu, terminal ve daha fazlasi. Uretkenliginizi artirin.',
    keywords: 'Ochtopus ozellikleri, AI kod tamamlama, yapay zeka asistan, kod editoru ozellikleri, IDE ozellikleri',
    canonical: '/features',
  },
  download: {
    title: 'Indir - macOS, Windows ve Linux',
    description: 'Ochtopus\'u macOS, Windows ve Linux icin indirin. Ucretsiz baslatin, saniyeler icinde kurulum tamamlanir.',
    keywords: 'Ochtopus indir, kod editoru indir, macOS IDE, Windows IDE, Linux IDE, ucretsiz kod editoru',
    canonical: '/download',
  },
  pricing: {
    title: 'Fiyatlandirma - Ucretsiz ve Pro Planlar',
    description: 'Ochtopus fiyatlandirma planlari. Ucretsiz baslatin veya Pro ozelliklere erisim icin upgrade edin. Kurumsal planlar da mevcuttur.',
    keywords: 'Ochtopus fiyatlandirma, ucretsiz kod editoru, Pro plan, kurumsal IDE',
    canonical: '/pricing',
  },
  enterprise: {
    title: 'Kurumsal - Ekipler icin Guclu Cozumler',
    description: 'Kurumsal seviyede guvenlik, SSO, SCIM ve ozel dagitim secenekleri. Ekibinizin verimliliini artirin.',
    keywords: 'kurumsal IDE, ekip kod editoru, SSO, SCIM, on-premise IDE, kurumsal yazilim gelistirme',
    canonical: '/enterprise',
  },
  community: {
    title: 'Topluluk - Gelistiricilerle Baglanti Kurun',
    description: 'Ochtopus gelistirici topluluuna katilip. Discord, GitHub ve daha fazlasinda binlerce gelistiriciyle baglanti kurun.',
    keywords: 'Ochtopus topluluk, gelistirici forum, Discord, GitHub, acik kaynak, katki',
    canonical: '/community',
  },
  changelog: {
    title: 'Degisiklik Kaydi - Surum Notlari ve Guncellemeler',
    description: 'Ochtopus degisiklik kaydi. Yeni ozellikleri, iyilestirmeleri ve hata duzeltmelerini gorun.',
    keywords: 'Ochtopus degisiklik kaydi, surum notlari, guncellemeler, yeni ozellikler',
    canonical: '/changelog',
  },
  blog: {
    title: 'Blog - Gelistirici Haberleri ve Ipuclari',
    description: 'Ochtopus blog. Gelistirici haberleri, kodlama ipuclari, urunle ilgili guncellemeler ve daha fazlasi.',
    keywords: 'Ochtopus blog, gelistirici haberleri, kodlama ipuclari, yazilim gelistirme',
    canonical: '/blog',
  },
  about: {
    title: 'Hakkimizda - Misyonumuz ve Ekibimiz',
    description: 'Ochtopus hakkinda bilgi edinin. Misyonumuz, vizyon ve arkasindaki ekip.',
    keywords: 'Ochtopus hakkinda, misyon, vizyon, ekip, sirket',
    canonical: '/about',
  },
  careers: {
    title: 'Kariyer - Bize Katilin',
    description: 'Ochtopus\'ta kariyer firsatlari. Kodlamanin geleceini sekillendirmemize yardim edin.',
    keywords: 'Ochtopus kariyer, is ilanlari, yazilim muhendisi is, startup kariyerleri',
    canonical: '/careers',
  },
  contact: {
    title: 'Iletisim - Bize Ulasin',
    description: 'Ochtopus ile iletisime gecin. Satis, destek veya genel sorulariniz icin bize ulasin.',
    keywords: 'Ochtopus iletisim, destek, satis, yardim',
    canonical: '/contact',
  },
  privacy: {
    title: 'Gizlilik Politikasi',
    description: 'Ochtopus gizlilik politikasi. Verilerinizi nasil topladigimizi, kullandigimizi ve korudugumuzu ogrenin.',
    keywords: 'Ochtopus gizlilik, gizlilik politikasi, veri koruma, KVKK',
    canonical: '/privacy',
  },
  terms: {
    title: 'Kullanim Sartlari',
    description: 'Ochtopus kullanim sartlari. Hizmetlerimizi kullanmak icin sartlar ve kosullar.',
    keywords: 'Ochtopus kullanim sartlari, hizmet sartlari, kosullar',
    canonical: '/terms',
  },
  security: {
    title: 'Guvenlik - Verilerinizi Koruyoruz',
    description: 'Ochtopus guvenlik uygulamalari. SOC 2, GDPR uyumlulugu ve kurumsal seviyede veri koruma.',
    keywords: 'Ochtopus guvenlik, SOC 2, GDPR, veri koruma, siber guvenlik',
    canonical: '/security',
  },
  docs: {
    title: 'Dokumantasyon - Baslangi ve API Referansi',
    description: 'Ochtopus dokumantasyonu. Baslangic rehberleri, API referansi ve kullanim kilavuzlari.',
    keywords: 'Ochtopus dokumantasyon, API, kilavuz, rehber, nasil yapilir',
    canonical: '/docs',
  },
  status: {
    title: 'Sistem Durumu',
    description: 'Ochtopus sistem durumu. Hizmetlerimizin anlık calisma durumunu kontrol edin.',
    keywords: 'Ochtopus durum, sistem durumu, uptime, servis durumu',
    canonical: '/status',
    noIndex: true,
  },
  dashboard: {
    title: 'Dashboard',
    description: 'Ochtopus kullanici paneli. Hesabinizi ve aboneliginizi yonetin.',
    keywords: 'Ochtopus dashboard, hesap, ayarlar',
    canonical: '/dashboard',
    noIndex: true,
  },
};
